import { pathToRegexp, compile } from 'path-to-regexp';
import React from 'react';
import qs from 'qs';
/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

var _assign = function __assign() {
  _assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }

    return t;
  };

  return _assign.apply(this, arguments);
};

function __rest(s, e) {
  var t = {};

  for (var p in s) {
    if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  }

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}

function __spreadArrays() {
  for (var s = 0, i = 0, il = arguments.length; i < il; i++) {
    s += arguments[i].length;
  }

  for (var r = Array(s), k = 0, i = 0; i < il; i++) {
    for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++) {
      r[k] = a[j];
    }
  }

  return r;
}

var RouterContext = React.createContext({
  query: {},
  fullPath: window.location.pathname,
  matches: [],
  params: {},
  routeParams: {},
  unmatched: "",
  navigate: function navigate(url) {
    window.location.href = url;
  },
  navigateParams: function navigateParams() {},
  update: function update() {},
  subroutes: {
    route: "ROOT",
    subroutes: [],
    matched: null,
    notFound: false
  },
  notFound: function notFound() {},
  setSubroutes: function setSubroutes() {}
});

function subrouteReducer(state, action) {
  var newState;
  var existing = state.subroutes.find(function (sr) {
    return sr.route === action.route;
  });

  if (existing) {
    newState = _assign(_assign({}, state), {
      subroutes: state.subroutes.map(function (sr) {
        return sr.route === action.route ? action : sr;
      })
    });
  } else {
    newState = _assign(_assign({}, state), {
      subroutes: __spreadArrays(state.subroutes, [action])
    });
  }

  newState.matched = newState.subroutes.length === 0 && newState.matched || !!newState.subroutes.find(function (sr) {
    return sr.matched;
  });
  return newState;
}

function useSubrouteReducer(defaultState) {
  return React.useReducer(subrouteReducer, defaultState);
}

function useRouter() {
  return React.useContext(RouterContext);
}

var Router = function Router(_a) {
  var children = _a.children;

  var _b = React.useState(window.location.href),
      setHref = _b[1];

  var update = React.useCallback(function () {
    return setHref(window.location.href);
  }, [window.location.href]);
  var pathname = window.location.pathname;
  React.useEffect(function () {
    var onpopstatebefore = window.onpopstate;
    var pushStateBefore = window.history.pushState;

    window.history.pushState = function (data, title, url) {
      pushStateBefore.call(this, data, title, url);
      update();
    };

    window.onpopstate = function (event) {
      if (onpopstatebefore) onpopstatebefore.call(this, event);
      update();
    };

    return function () {
      window.history.pushState = pushStateBefore;
      window.onpopstate = onpopstatebefore;
    };
  }, []);
  var navigate = React.useCallback(function (path, query, options) {
    var _a;

    var stringifiedQuery = query ? qs.stringify(query) : null;
    var pathWithQuery = stringifiedQuery ? [path, stringifiedQuery].join("?") : path;

    if ((_a = options) === null || _a === void 0 ? void 0 : _a.replace) {
      window.history.replaceState({}, "", pathWithQuery);
    } else {
      window.history.pushState({}, "", pathWithQuery);
    }
  }, []);
  var defaultState = useRouter();

  var _c = useSubrouteReducer(defaultState.subroutes),
      subroutes = _c[0],
      setSubroutes = _c[1];

  var initialState = _assign(_assign({}, defaultState), {
    query: window.location.search ? qs.parse(window.location.search.slice(1)) : {},
    fullPath: pathname,
    update: update,
    navigate: navigate,
    unmatched: pathname,
    subroutes: subroutes,
    setSubroutes: setSubroutes
  });

  return React.createElement(RouterContext.Provider, {
    value: initialState
  }, children);
};

var useRoute = function useRoute(argument) {
  var options = typeof argument === "string" ? {
    path: argument
  } : argument;
  var path = options.path,
      exact = options.exact;

  var _a = React.useMemo(function () {
    var keys = [];
    return [keys, pathToRegexp(path, keys, {
      end: !!exact
    })];
  }, [path, exact]),
      keys = _a[0],
      regexp = _a[1];

  var route = useRouter();
  var matches = regexp.exec(route.unmatched);
  var namedMatches = matches ? matches.slice(1).map(function (m, i) {
    return {
      key: keys[i],
      match: m
    };
  }) : [];
  var fullMatchedRoute = [route.fullMatchedRoute, path].join("");

  var allMatches = __spreadArrays(route.matches, namedMatches);

  var routeParams = allMatches.reduce(function (o, _a) {
    var _b;

    var key = _a.key,
        match = _a.match;
    return key && key.name ? _assign(_assign({}, o), (_b = {}, _b[key.name] = match, _b)) : o;
  }, {});

  var params = _assign(_assign({}, routeParams), route.query);

  var navigateParams = React.useCallback(function (newParams) {
    var reverse = compile(fullMatchedRoute);
    var newQuery = Object.keys(newParams).reduce(function (queries, key) {
      var _a;

      return routeParams.hasOwnProperty(key) ? queries : _assign(_assign({}, queries), (_a = {}, _a[key] = newParams[key], _a));
    }, route.query);
    route.navigate(reverse(_assign(_assign({}, routeParams), newParams)), newQuery);
  }, [fullMatchedRoute, routeParams, route.query, route.navigate]);

  var _b = React.useState(false),
      notFound = _b[0],
      setNotFound = _b[1];

  var _c = useSubrouteReducer({
    route: path,
    subroutes: [],
    matched: !!matches,
    notFound: false
  }),
      subroutes = _c[0],
      setSubroutes = _c[1];

  React.useEffect(function () {
    route.setSubroutes(_assign(_assign({}, subroutes), {
      notFound: notFound
    }));
  }, [path, subroutes, notFound]);

  if (route.subroutes.matched !== false && matches) {
    return _assign(_assign({}, route), {
      notFound: function notFound() {
        setNotFound(true);
      },
      subroutes: subroutes,
      setSubroutes: setSubroutes,
      matches: allMatches,
      routeParams: routeParams,
      params: params,
      fullMatchedRoute: fullMatchedRoute,
      navigateParams: navigateParams,
      unmatched: route.unmatched.slice(matches[0].length) || "/"
    });
  } else {
    return null;
  }
};

var Route = function Route(_a) {
  var path = _a.path,
      exact = _a.exact,
      children = _a.children;
  var route = useRoute({
    path: path,
    exact: exact
  });

  if (route) {
    return React.createElement(RouterContext.Provider, {
      value: route
    }, typeof children === "function" ? children(route.params) : children);
  } else return null;
};

var join = function join(base, part) {
  return base + "/" + part.replace(/^\//, "");
};

var relativeHref = function relativeHref(base, relative) {
  if (relative.startsWith("./")) {
    return join(base, relative.slice(1));
  } else if (relative.startsWith("../")) {
    var stripped = base.replace(/\/[^\/]+$/, "");
    return relativeHref(stripped, relative.slice(1));
  } else return relative;
};

var Link = function Link(_a) {
  var href = _a.href,
      children = _a.children,
      _b = _a.currentClassName,
      currentClassName = _b === void 0 ? "active" : _b,
      className = _a.className,
      exact = _a.exact,
      onClick = _a.onClick,
      props = __rest(_a, ["href", "children", "currentClassName", "className", "exact", "onClick"]);

  var _c = useRouter(),
      navigate = _c.navigate,
      fullPath = _c.fullPath;

  var url = relativeHref(fullPath, href);
  var linkClick = React.useCallback(function (evt) {
    if (onClick) onClick(evt);
    evt.preventDefault();
    navigate(url);
  }, [navigate, onClick, url]);
  var isCurrent = exact ? fullPath === url : fullPath.startsWith(url);

  if (typeof children === "function") {
    return children({
      onClick: linkClick,
      current: isCurrent,
      url: url
    });
  } else {
    return React.createElement("a", _assign({
      className: (isCurrent ? currentClassName : "") + " " + className,
      href: url,
      onClick: linkClick
    }, props), children);
  }
};

var Redirect = function Redirect(_a) {
  var to = _a.to;
  var navigate = useRouter().navigate;
  React.useEffect(function () {
    navigate(to, undefined, {
      replace: true
    });
  });
  return null;
};

function anyNotFound(state) {
  return state.notFound || !!state.subroutes.find(function (sr) {
    return anyNotFound(sr);
  });
}

function NotFound(_a) {
  var children = _a.children;
  var subroutes = useRouter().subroutes;

  if (anyNotFound(subroutes) || subroutes.matched === false) {
    return React.createElement(React.Fragment, null, children);
  } else return null;
}

export { Link, NotFound, Redirect, Route, Router, RouterContext, useRoute, useRouter };